import { useEffect, useState } from 'react';
import {
  sendOffertePdf,
  type OfferteContext,
  type SendOffertePdfInput,
} from '../../lib/offerte-pdf';
import type { OffertePackage } from '../../lib/offertes';
import { formatCurrency } from '../../lib/format';
import { AanFieldPicker } from '../inbox/AanFieldPicker';

interface SendPdfDialogProps {
  isOpen: boolean;
  leadId: string;
  leadnummer: string;
  brand: string;
  customerName: string;
  customerEmail: string;
  packages?: OffertePackage[];
  defaultPackageSku?: string;
  onClose: () => void;
  onSent: (result: { contentDocumentId: string | null; resendId: string | null }) => void;
}

const CONTEXT_OPTIONS: { value: OfferteContext; label: string }[] = [
  { value: 'consumer', label: 'Consument' },
  { value: 'b2b', label: 'Zakelijk (B2B)' },
  { value: 'verzekering', label: 'Verzekering' },
];

function defaultBody(brand: string, customerName: string, leadnummer: string): string {
  const greeting = customerName ? `Beste ${customerName.split(' ')[0]},` : 'Beste klant,';
  const sender = brand === 'KLEDA' ? 'Kleda' : 'Cinco Cleaning';
  return `${greeting}

Bij deze ontvang je je offerte (${leadnummer}) als PDF in de bijlage.

Heb je vragen of wil je de offerte aanpassen? Reageer gerust op deze mail of bel ons.

Met vriendelijke groet,
${sender}`;
}

export function SendPdfDialog({
  isOpen,
  leadId,
  leadnummer,
  brand,
  customerName,
  customerEmail,
  packages = [],
  defaultPackageSku = '',
  onClose,
  onSent,
}: SendPdfDialogProps) {
  const [to, setTo] = useState('');
  const [cc, setCc] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [context, setContext] = useState<OfferteContext>('consumer');
  const [packageSku, setPackageSku] = useState<string>('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setTo(customerEmail || '');
    setCc('');
    setSubject(`Offerte ${leadnummer}`);
    setBody(defaultBody(brand, customerName, leadnummer));
    setContext('consumer');
    setPackageSku(defaultPackageSku || '');
    setNote('');
    setError(null);
    setBusy(false);
  }, [isOpen, customerEmail, customerName, leadnummer, brand, defaultPackageSku]);

  if (!isOpen) return null;

  const recipients = to
    .split(/[,;]/)
    .map((s) => s.trim())
    .filter(Boolean);
  const ccRecipients = cc
    .split(/[,;]/)
    .map((s) => s.trim())
    .filter(Boolean);
  const canSubmit = recipients.length > 0 && subject.trim().length > 0 && !busy;

  async function handleSend() {
    if (!canSubmit) return;
    setBusy(true);
    setError(null);
    try {
      const input: SendOffertePdfInput = {
        to: recipients,
        cc: ccRecipients.length > 0 ? ccRecipients : undefined,
        subject: subject.trim(),
        body: body.trim(),
        context,
        packageSku: packageSku || undefined,
        note: note.trim() || undefined,
      };
      const result = await sendOffertePdf(leadId, input);
      onSent({
        contentDocumentId: result.contentDocumentId,
        resendId: result.resendId,
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Versturen mislukt');
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-[60]" />
      <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 pointer-events-none">
        <div className="bg-white rounded-xl shadow-2xl w-full max-w-xl pointer-events-auto flex flex-col max-h-[90vh]">
          <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between flex-shrink-0">
            <div>
              <h3 className="text-base font-semibold text-slate-900">Offerte mailen</h3>
              <p className="text-xs text-slate-500 mt-0.5">
                {leadnummer} · PDF wordt als bijlage meegestuurd en gearchiveerd in Salesforce.
              </p>
            </div>
            <button
              type="button"
              onClick={busy ? undefined : onClose}
              disabled={busy}
              aria-label="Sluiten"
              className="text-slate-400 hover:text-slate-600 disabled:opacity-50"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="px-5 py-4 space-y-4 overflow-y-auto">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">
                Aan <span className="text-red-500">*</span>
              </label>
              {/* Lijstveld: een keuze uit het adresboek vervangt alleen het
                  segment waar je in typt, dus eerdere ontvangers blijven staan. */}
              <AanFieldPicker
                variant="stacked"
                multiple
                value={to}
                onChange={setTo}
                disabled={busy}
              />
              <p className="text-[11px] text-slate-400 mt-1">
                Meerdere ontvangers? Scheid met komma of puntkomma.
              </p>
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">CC (optioneel)</label>
              <input
                type="text"
                value={cc}
                onChange={(e) => setCc(e.target.value)}
                placeholder="copy@voorbeeld.nl"
                className="w-full px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                disabled={busy}
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">
                Onderwerp <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                disabled={busy}
              />
            </div>

            <div className={packages.length > 0 ? 'grid grid-cols-2 gap-3' : ''}>
              {packages.length > 0 && (
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Pakket in PDF</label>
                  <select
                    value={packageSku}
                    onChange={(e) => setPackageSku(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    disabled={busy}
                  >
                    {packages.map((pkg) => (
                      <option key={pkg.sku} value={pkg.sku}>
                        {pkg.label}
                        {pkg.priceDifference > 0
                          ? ` (+${formatCurrency(pkg.priceDifference)})`
                          : ''}
                      </option>
                    ))}
                  </select>
                  <p className="text-[11px] text-slate-400 mt-1">
                    Welk pakket wordt uitgelicht in de PDF.
                  </p>
                </div>
              )}
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">PDF-context</label>
                <select
                  value={context}
                  onChange={(e) => setContext(e.target.value as OfferteContext)}
                  className="w-full px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  disabled={busy}
                >
                  {CONTEXT_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                <p className="text-[11px] text-slate-400 mt-1">
                  Verzekering toont een extra disclaimer in de PDF.
                </p>
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">
                Toelichting in PDF (optioneel)
              </label>
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={4}
                maxLength={4000}
                placeholder="Bv. specifieke afspraken, opslag-condities, of een persoonlijke noot — verschijnt in de PDF zelf, onder de specificatie."
                className="w-full px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                disabled={busy}
              />
              <p className="text-[11px] text-slate-400 mt-1">
                Komt in de PDF zelf — niet alleen in de mail. Lege regel = nieuwe alinea.
              </p>
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Bericht in mail</label>
              <textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                rows={8}
                className="w-full px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 font-mono"
                disabled={busy}
              />
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-md px-3 py-2 text-xs text-red-700">
                {error}
              </div>
            )}
          </div>

          <div className="px-5 py-3 border-t border-slate-200 flex justify-end gap-2 flex-shrink-0">
            <button
              type="button"
              onClick={onClose}
              disabled={busy}
              className="px-3 py-1.5 text-sm font-medium text-slate-600 border border-slate-300 rounded-md hover:bg-slate-50 disabled:opacity-50"
            >
              Annuleren
            </button>
            <button
              type="button"
              onClick={handleSend}
              disabled={!canSubmit}
              className="px-3 py-1.5 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50"
            >
              {busy ? 'Versturen...' : 'Verstuur PDF'}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
